// @ts-nocheck
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import moment from 'moment';

import { API_ENDPOINT, BTSessionStatus } from '../constants';
import { fetchWrapper, toUtcOffsetDisplay } from '../helpers';
import { getSessionSearchStr } from '../utils/common';
import { asDefinedOr } from '../components/layout/billings/claims/utils';
import { ServiceCodes, SessionClaimStatus } from '../components/layout/billings/claims/constants';
import { CQAReviewStatus } from '../components/layout/qa/constants';

import type {
  ISessionReviewSummary,
  IClinicalQAReview,
  IClinicalQAReviewDiscourse,
  ISessionWithClaimStatus,
  IBTSession,
  ISessionMinified,
  ISessionMinifiedWithClaimHistory,
  IAwaitingAcknowledgement,
  EVVTokenKind,
  IEVVCaptureDesktopPayload,
  IEVVCaptureGpsPayload,
  IEVVCaptureGpsResponse,
  IEVVSessionStatus,
  IEVVTokenCreateResponse,
  ISessionPracticedTargetTrial,
  IDataDashboardGoalOption,
  IDataDashboardOptionsRequest,
} from '../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

export interface SessionState {
  list: Array<ISessionMinified>;
  filteredList: Array<IBTSession>;
  unacknowledgedSessions: Array<IAwaitingAcknowledgement>;
  current: IBTSession | null;
  error: any;
  loading: boolean;
  timer: any;
  trialLoading: boolean;
  clinicalReview: IClinicalQAReview | null;
  reviewLoading: boolean;
  notificationsLoading: boolean;
  trialSummary: any[];
  multiTrialSummary: any[];
  multiTrialSummaryLoading: boolean;
  multiTrialSummaryError: string | null;
  multiTrialSummaryRequestId: string | null;
  dashboardOptions: IDataDashboardGoalOption[];
  dashboardOptionsLoading: boolean;
  dashboardOptionsError: string | null;
  dashboardOptionsRequestId: string | null;
  reviewSummaryLoading: boolean;
  reviewSummary: ISessionReviewSummary[];
  sessionsWithClaimStatus: ISessionWithClaimStatus[];
  sessionsReadyForQA: Array<ISessionMinifiedWithClaimHistory>;
  gptNarrativeFlag: number;
  narrativeSuggestionLoading: boolean;
  narrativeSuggestionRequestId: string | null;
  narrativeContextToken: string | null;
  narrativeUpdateRequest: {
    sessionId?: string;
    contextToken?: string;
    requestId: string;
  } | null;
  // Latest dispatched getSession fetch. Guards fulfilled/rejected so a stale/out-of-order
  // detail response (e.g. session A's response arriving after session B is already active)
  // never overwrites the currently active session.
  detailRequestId: string | null;
  awaitingGuardianSignatureSession: IBTSession[];
}

export interface QueryState {
  providerId?: number;
  clientId?: number;
  startDate?: any;
  endDate?: any;
  statusId?: any;
}

const name = 'sessions';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<SessionState>) => createExtraReducers(builder),
});

function createInitialState(): SessionState {
  return {
    list: [],
    filteredList: [],
    unacknowledgedSessions: [],
    current: null,
    loading: false,
    error: null,
    timer: null,
    trialLoading: false,
    clinicalReview: null,
    reviewLoading: false,
    notificationsLoading: false,
    trialSummary: [],
    multiTrialSummary: [],
    multiTrialSummaryLoading: false,
    multiTrialSummaryError: null,
    multiTrialSummaryRequestId: null,
    dashboardOptions: [],
    dashboardOptionsLoading: false,
    dashboardOptionsError: null,
    dashboardOptionsRequestId: null,
    reviewSummary: [],
    reviewSummaryLoading: false,
    sessionsWithClaimStatus: [],
    sessionsReadyForQA: [],
    gptNarrativeFlag: 0,
    narrativeSuggestionLoading: false,
    narrativeSuggestionRequestId: null,
    narrativeContextToken: null,
    narrativeUpdateRequest: null,
    detailRequestId: null,
    awaitingGuardianSignatureSession: [],
  };
}

function createReducers() {
  return {
    clearSession,
    clearDashboardOptions,
    updateTimer,
    updateState,
    setNarrativeContext,
  };

  function clearSession(state: SessionState) {
    state.filteredList = [];
    state.current = null;
    state.timer = null;
    state.narrativeContextToken = null;
    state.narrativeUpdateRequest = null;
    state.narrativeSuggestionLoading = false;
    state.narrativeSuggestionRequestId = null;
    state.detailRequestId = null;
  }

  function clearDashboardOptions(state: SessionState) {
    state.dashboardOptions = [];
    state.dashboardOptionsLoading = false;
    state.dashboardOptionsError = null;
    state.dashboardOptionsRequestId = null;
  }

  function updateTimer(state: SessionState) {
    state.timer = null;
  }

  function updateState(state: SessionState, action: any) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function setNarrativeContext(
    state: SessionState,
    action: PayloadAction<{ sessionId?: string; contextToken: string }>,
  ) {
    if (state.current?.id !== action.payload.sessionId) return;
    state.narrativeContextToken = action.payload.contextToken;
  }
}

function updateTrialInSession(session: IBTSession, updatedTrial: ISessionPracticedTargetTrial) {
  if (Array.isArray(session.goals_and_trials)) return;

  const goalId = Object.keys(session.goals_and_trials).find((key) =>
    session.goals_and_trials[key].trials.some(
      (trial: ISessionPracticedTargetTrial) => trial.id === updatedTrial.id,
    ),
  );
  if (!goalId) return;

  session.goals_and_trials[goalId].trials = session.goals_and_trials[goalId].trials.map(
    (trial: ISessionPracticedTargetTrial) =>
      trial.id === updatedTrial.id ? updatedTrial : trial,
  );
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/sessions`;

  return {
    getList: getList(),
    startSession: startSession(),
    saveBTSignartures: saveBTSignartures(),
    saveGuardianSignartures: saveGuardianSignartures(),
    saveBay7InPersonGuardianSignature: saveBay7InPersonGuardianSignature(),
    updateSessionHeaderInfo: updateSessionHeaderInfo(),
    getSession: getSession(),
    reviewSession: reviewSession(),
    createTrial: createTrial(),
    updateTrial: updateTrial(),
    deleteTrial: deleteTrial(),
    downloadSessionNote: downloadSessionNote(),
    createCQAReview: createCQAReview(),
    updateCQAReview: updateCQAReview(),
    updateSymptomsInterventions: updateSymptomsInterventions(),
    updateProgressSynopsis: updateProgressSynopsis(),
    updateSessionNarrative: updateSessionNarrative(),
    stopCQAReview: stopCQAReview(),
    acknowledgeCQAReview: acknowledgeCQAReview(),
    discourseCQAReview: discourseCQAReview(),
    getUnacknowledgedSessions: getUnacknowledgedSessions(),
    getTrialSummary: getTrialSummary(),
    getMultiTrialSummary: getMultiTrialSummary(),
    getDashboardOptions: getDashboardOptions(),
    getReviewSummary: getReviewSummary(),
    downloadSessionNoteReview: downloadSessionNoteReview(),
    deleteClinicalReview: deleteClinicalReview(),
    changeSessionStatus: changeSessionStatus(),
    getSessionsWithClaimStatus: getSessionsWithClaimStatus(),
    getSessionNarrativeSuggestion: getSessionNarrativeSuggestion(),
    getAwaitingGuardianSignatureSession: getAwaitingGuardianSignatureSession(),
    createStartEvvToken: createStartEvvToken(),
    createEndEvvToken: createEndEvvToken(),
    getEvvStatus: getEvvStatus(),
    captureEvvGps: captureEvvGps(),
    captureEvvDesktop: captureEvvDesktop(),
    deleteSession: deleteSession(),
    endSessionEarly: endSessionEarly(),
    createRateEvent: createRateEvent(),
    updateRateEvent: updateRateEvent(),
    deleteRateEvent: deleteRateEvent(),
    createDurationEvent: createDurationEvent(),
    updateDurationEvent: updateDurationEvent(),
    deleteDurationEvent: deleteDurationEvent(),
    makeSessionEditable: makeSessionEditable(),
    backdateSession: backdateSession(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSessionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSessionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });

        return response;
      },
    );
  }

  function startSession() {
    return createAsyncThunk(
      `${name}/startSession`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function createStartEvvToken() {
    return createAsyncThunk(
      `${name}/createStartEvvToken`,
      async ({
        sessionId,
        replaceActive,
        getAccessTokenSilently,
      }: any): Promise<IEVVTokenCreateResponse> =>
        await fetchWrapper.post({
          url: `${baseUrl}/${sessionId}/evv/start${replaceActive ? '?replace_active=true' : ''}`,
          getAccessTokenSilently,
        }),
    );
  }

  function createEndEvvToken() {
    return createAsyncThunk(
      `${name}/createEndEvvToken`,
      async ({
        sessionId,
        replaceActive,
        getAccessTokenSilently,
      }: any): Promise<IEVVTokenCreateResponse> =>
        await fetchWrapper.post({
          url: `${baseUrl}/${sessionId}/evv/end${replaceActive ? '?replace_active=true' : ''}`,
          getAccessTokenSilently,
        }),
    );
  }

  function getEvvStatus() {
    return createAsyncThunk(
      `${name}/getEvvStatus`,
      async ({ sessionId, getAccessTokenSilently }: any): Promise<IEVVSessionStatus> =>
        await fetchWrapper.get({
          url: `${baseUrl}/${sessionId}/evv/status`,
          getAccessTokenSilently,
        }),
    );
  }

  function captureEvvGps() {
    return createAsyncThunk(
      `${name}/captureEvvGps`,
      async ({
        token,
        data,
      }: {
        token: string;
        data: IEVVCaptureGpsPayload;
      }): Promise<IEVVCaptureGpsResponse> =>
        await fetchWrapper.post(
          {
            url: `${API_ENDPOINT}/evv/capture_gps`,
            headers: { 'X-EVV-Token': token },
          },
          data,
        ),
    );
  }

  function captureEvvDesktop() {
    return createAsyncThunk(
      `${name}/captureEvvDesktop`,
      async ({
        sessionId,
        kind,
        data,
        getAccessTokenSilently,
      }: {
        sessionId: number | string;
        kind: EVVTokenKind;
        data: IEVVCaptureDesktopPayload;
        getAccessTokenSilently: () => Promise<string>;
      }): Promise<IEVVCaptureGpsResponse> =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${sessionId}/evv/${kind}/desktop`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function saveBTSignartures() {
    return createAsyncThunk(
      `${name}/saveBTSignartures`,
      async ({ sessionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          { url: `${baseUrl}/${sessionId}/stop/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function endSessionEarly() {
    return createAsyncThunk(
      `${name}/endSessionEarly`,
      async ({ sessionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch({
          url: `${baseUrl}/${sessionId}/terminate/`,
          getAccessTokenSilently,
        }),
    );
  }

  function saveBay7InPersonGuardianSignature() {
    return createAsyncThunk(
      `${name}/saveBay7InPersonGuardianSignature`,
      async ({ data, getAccessTokenSilently }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/bay7/guardian_signature/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function saveGuardianSignartures() {
    return createAsyncThunk(
      `${name}/saveGuardianSignartures`,
      async ({ sessionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          { url: `${baseUrl}/${sessionId}/guardian_sign/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function updateSessionHeaderInfo() {
    return createAsyncThunk(
      `${name}/updateSessionInfo`,
      async ({ sessionId, data, getAccessTokenSilently }: any, { getState }: any) => {
        const currentSession = getState().sessions.current;
        const requestData = currentSession
          ? {
              client_id: currentSession.client_id,
              date_start: moment(currentSession.date_start).toISOString(),
              expected_duration: currentSession.expected_duration,
              is_supervised: currentSession.is_supervised,
              location: currentSession.location,
              delivery_method: currentSession.delivery_method,
              ...data,
            }
          : data;

        return await fetchWrapper.patch(
          { url: `${baseUrl}/${sessionId}/`, getAccessTokenSilently },
          requestData,
        );
      },
    );
  }

  function updateSymptomsInterventions() {
    return createAsyncThunk(
      `${name}/updateSymptomsInterventions`,
      async ({ sessionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${sessionId}/symptoms-interventions/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function updateProgressSynopsis() {
    return createAsyncThunk(
      `${name}/updateProgressSynopsis`,
      async ({ sessionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${sessionId}/progress-synopsis/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function updateSessionNarrative() {
    return createAsyncThunk(
      `${name}/updateSessionNarrative`,
      async ({ sessionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${sessionId}/narrative/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function getSession() {
    return createAsyncThunk(
      `${name}/getSession`,
      async ({ sessionId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({ url: `${baseUrl}/${sessionId}/`, getAccessTokenSilently });
      },
    );
  }

  function getAwaitingGuardianSignatureSession() {
    return createAsyncThunk(
      `${name}/getAwaitingGuardianSignatureSession`,
      async ({ getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/awaiting_guardian_signature/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function reviewSession() {
    return createAsyncThunk(
      `${name}/reviewSession`,
      async ({ sessionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.put(
          { url: `${baseUrl}/${sessionId}/review/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function createTrial() {
    return createAsyncThunk(
      `${name}/createTrial`,
      async ({ sessionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/${sessionId}/trials/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function updateTrial() {
    return createAsyncThunk(
      `${name}/updateTrial`,
      async ({ sessionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.patch(
          { url: `${baseUrl}/${sessionId}/trials/${data.id}/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function deleteTrial() {
    return createAsyncThunk(
      `${name}/deleteTrial`,
      async ({ sessionId, trialId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${sessionId}/trials/${trialId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function downloadSessionNote() {
    return createAsyncThunk(
      `${name}/downloadSessionNote`,
      async ({ sessionId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${sessionId}/download/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createCQAReview() {
    return createAsyncThunk(
      `${name}/createCQAReview`,
      async ({ sessionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/${sessionId}/clinical_qa_reviews/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function updateCQAReview() {
    return createAsyncThunk(
      `${name}/updateCQAReview`,
      async ({ sessionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.patch(
          {
            url: `${baseUrl}/${sessionId}/clinical_qa_reviews/${data.id}/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function stopCQAReview() {
    return createAsyncThunk(
      `${name}/stopCQAReview`,
      async ({ sessionId, reviewId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.put(
          {
            url: `${baseUrl}/${sessionId}/clinical_qa_reviews/${reviewId}/stop/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function acknowledgeCQAReview() {
    return createAsyncThunk(
      `${name}/acknowledgeCQAReview`,
      async ({ sessionId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          {
            url: `${baseUrl}/${sessionId}/clinical_qa_reviews/${data.id}/acknowledge/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function discourseCQAReview() {
    return createAsyncThunk(
      `${name}/discourseCQAReview`,
      async ({ sessionId, reviewId, data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          {
            url: `${baseUrl}/${sessionId}/clinical_qa_reviews/${reviewId}/discourses/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function getUnacknowledgedSessions() {
    return createAsyncThunk(
      `${name}/getUnacknowledgedSessions`,
      async ({ getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/awaiting_acknowledgement/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function getTrialSummary() {
    return createAsyncThunk(
      `${name}/trialSummary`,
      async ({ params, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/data/trial_summary/?client_id=${params.client_id}&start_date=${params.start_date}&end_date=${params.end_date}&goal_id=${params.goal_id}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getMultiTrialSummary() {
    return createAsyncThunk(
      `${name}/multiTrialSummary`,
      async ({ params, getAccessTokenSilently }: any) => {
        const promises = params.goal_ids.map((goal_id: number) => {
          const query = new URLSearchParams({
            client_id: String(params.client_id),
            start_date: params.start_date,
            end_date: params.end_date,
            goal_id: String(goal_id),
          });
          if (params.duration_mode) query.set('duration_mode', params.duration_mode);
          if (params.treatment_plan_id) {
            query.set('treatment_plan_id', String(params.treatment_plan_id));
          }
          return fetchWrapper.get({
            url: `${baseUrl}/data/trial_summary/?${query}`,
            getAccessTokenSilently,
          });
        });
        const response = await Promise.all(promises);
        return response;
      },
    );
  }

  function getDashboardOptions() {
    return createAsyncThunk<IDataDashboardGoalOption[], IDataDashboardOptionsRequest>(
      `${name}/dashboardOptions`,
      async ({ params, getAccessTokenSilently }: IDataDashboardOptionsRequest, { signal }) => {
        const query = new URLSearchParams({
          client_id: String(params.client_id),
          start_date: params.start_date,
          end_date: params.end_date,
        });
        if (params.treatment_plan_id) {
          query.set('treatment_plan_id', String(params.treatment_plan_id));
        }
        return await fetchWrapper.get({
          url: `${baseUrl}/data/dashboard_options/?${query}`,
          getAccessTokenSilently,
          signal,
        });
      },
    );
  }

  function getReviewSummary() {
    return createAsyncThunk(
      `${name}/getBtReviewSummary`,
      async ({ getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/data/review_summary/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function downloadSessionNoteReview() {
    return createAsyncThunk(
      `${name}/downloadSessionNoteReview`,
      async ({ sessionId, clinicalQAReviewId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${sessionId}/clinical_qa_reviews/${clinicalQAReviewId}/download/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function deleteClinicalReview() {
    return createAsyncThunk(
      `${name}/deleteClinicalReview`,
      async ({ sessionId, clinicalQAReviewId, getAccessTokenSilently, data }: any) => {
        const response = await fetchWrapper.delete(
          {
            url: `${baseUrl}/${sessionId}/clinical_qa_reviews/${clinicalQAReviewId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function changeSessionStatus() {
    return createAsyncThunk(
      `${name}/changeSessionStatus`,
      async ({ sessionId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch({
          url: `${baseUrl}/${sessionId}/reset_status/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getSessionsWithClaimStatus() {
    return createAsyncThunk(
      `${name}/claim_status`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/claim_history/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSessionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSessionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });

        return response;
      },
    );
  }

  function getSessionNarrativeSuggestion() {
    return createAsyncThunk(
      `${name}/narrativeSuggestion`,
      async ({ getAccessTokenSilently, sessionId }: any) => {
        const baseSearch = `${baseUrl}/${sessionId}/narrative_suggestion/`;
        const response = await fetchWrapper.post({
          url: baseSearch,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function deleteSession() {
    return createAsyncThunk(
      `${name}/deleteSession`,
      async ({ sessionId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${sessionId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createDurationEvent() {
    return createAsyncThunk(
      `${name}/createDurationEvent`,
      async ({ data, getAccessTokenSilently, sessionId, trailId }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${sessionId}/trials/${trailId}/duration_events/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function updateDurationEvent() {
    return createAsyncThunk(
      `${name}/updateDurationEvent`,
      async ({ data, getAccessTokenSilently, sessionId, trailId, durationEventId }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${sessionId}/trials/${trailId}/duration_events/${durationEventId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteDurationEvent() {
    return createAsyncThunk(
      `${name}/deleteDurationEvent`,
      async ({ getAccessTokenSilently, sessionId, trailId, durationEventId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${sessionId}/trials/${trailId}/duration_events/${durationEventId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function createRateEvent() {
    return createAsyncThunk(
      `${name}/createRateEvent`,
      async ({ data, getAccessTokenSilently, sessionId, trailId }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${sessionId}/trials/${trailId}/rate_events/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function updateRateEvent() {
    return createAsyncThunk(
      `${name}/updateRateEvent`,
      async ({ data, getAccessTokenSilently, sessionId, trailId, rateEventId }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${sessionId}/trials/${trailId}/rate_events/${rateEventId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteRateEvent() {
    return createAsyncThunk(
      `${name}/deleteRateEvent`,
      async ({ getAccessTokenSilently, sessionId, trailId, rateEventId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${sessionId}/trials/${trailId}/rate_events/${rateEventId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function makeSessionEditable() {
    return createAsyncThunk(
      `${name}/makeSessionEditable`,
      async ({ sessionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.put({
          url: `${baseUrl}/${sessionId}/edit/`,
          getAccessTokenSilently,
        }),
    );
  }

  function backdateSession() {
    return createAsyncThunk(
      `${name}/backdateSession`,
      async ({ sessionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          {
            url: `${baseUrl}/${sessionId}/backdate/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<SessionState>) {
  const {
    getList,
    startSession,
    saveBTSignartures,
    saveGuardianSignartures,
    saveBay7InPersonGuardianSignature,
    updateSessionHeaderInfo,
    getSession,
    reviewSession,
    createTrial,
    updateTrial,
    deleteTrial,
    createCQAReview,
    updateCQAReview,
    updateSymptomsInterventions,
    updateProgressSynopsis,
    updateSessionNarrative,
    stopCQAReview,
    acknowledgeCQAReview,
    discourseCQAReview,
    getUnacknowledgedSessions,
    getTrialSummary,
    getMultiTrialSummary,
    getDashboardOptions,
    getReviewSummary,
    deleteClinicalReview,
    changeSessionStatus,
    getSessionsWithClaimStatus,
    getSessionNarrativeSuggestion,
    getAwaitingGuardianSignatureSession,
    deleteSession,
    endSessionEarly,
    createRateEvent,
    updateRateEvent,
    deleteRateEvent,
    createDurationEvent,
    updateDurationEvent,
    deleteDurationEvent,
    makeSessionEditable,
    backdateSession,
  } = extraActions;
  builder
    // getList()
    .addCase(getList.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: SessionState, action: PayloadAction<IBTSession[]>) => {
      state.list = action.payload;
      state.filteredList = action.payload;
      state.loading = false;
    })
    .addCase(getList.rejected, (state: SessionState, action: any) => {
      state.list = [];
      state.filteredList = [];
      state.error = action.error;
      if (!action.meta?.arg?.signal?.aborted) {
        toast.error(action.error.message);
        state.loading = false;
      }
    })

    // getAwaitingGuardianSignatureSession()
    .addCase(getAwaitingGuardianSignatureSession.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getAwaitingGuardianSignatureSession.fulfilled,
      (state: SessionState, action: PayloadAction<IBTSession[]>) => {
        state.awaitingGuardianSignatureSession = action.payload;
        state.loading = false;
      },
    )
    .addCase(getAwaitingGuardianSignatureSession.rejected, (state: SessionState, action: any) => {
      state.awaitingGuardianSignatureSession = [];
      state.error = action.error;
      toast.error(action.error.message);
      state.loading = false;
    })

    // getSessionNarrativeSuggestion()
    .addCase(
      getSessionNarrativeSuggestion.pending,
      (state: SessionState, action: { meta: { requestId: string } }) => {
        state.narrativeSuggestionRequestId = action.meta.requestId;
        state.narrativeSuggestionLoading = true;
      },
    )
    .addCase(getSessionNarrativeSuggestion.fulfilled, (state: SessionState, action: any) => {
      if (action.meta.requestId !== state.narrativeSuggestionRequestId) return;
      state.narrativeSuggestionLoading = false;
    })
    .addCase(getSessionNarrativeSuggestion.rejected, (state: SessionState, action: any) => {
      if (action.meta.requestId !== state.narrativeSuggestionRequestId) return;
      state.narrativeSuggestionLoading = false;
    })

    // startSession()
    .addCase(startSession.pending, (state: SessionState) => {
      state.error = null;
    })
    .addCase(startSession.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.current = action.payload;
      if (!action.payload?.evv_required || action.payload?.evv_start_captured) {
        toast.success('Session started');
      }
    })
    .addCase(startSession.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(state.error.message, {
        toastId: 'session-note-creation-error',
      });
    })

    // saveBTSignartures()
    .addCase(saveBTSignartures.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(saveBTSignartures.fulfilled, (state: SessionState) => {
      state.loading = false;
    })
    .addCase(saveBTSignartures.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // saveBay7InPersonGuardianSignature()
    .addCase(saveBay7InPersonGuardianSignature.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(saveBay7InPersonGuardianSignature.fulfilled, (state: SessionState) => {
      state.loading = false;
    })
    .addCase(saveBay7InPersonGuardianSignature.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // saveGuardianSignartures()
    .addCase(saveGuardianSignartures.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(saveGuardianSignartures.fulfilled, (state: SessionState) => {
      toast.success('Session has been submitted', {
        toastId: 'session-note-creation-successful',
      });
      state.loading = false;
    })
    .addCase(saveGuardianSignartures.rejected, (state: SessionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // updateSymptomsInterventions()
    .addCase(updateSymptomsInterventions.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateSymptomsInterventions.fulfilled, (state: SessionState, action: any) => {
      state.current = {
        ...state.current,
        ...action.payload,
      };
      state.loading = false;
    })
    .addCase(updateSymptomsInterventions.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
      state.loading = false;
    })

    // updateProgressSynopsis()
    .addCase(updateProgressSynopsis.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateProgressSynopsis.fulfilled, (state: SessionState, action: any) => {
      state.current = {
        ...state.current,
        ...action.payload,
      };
      state.loading = false;
    })
    .addCase(updateProgressSynopsis.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
      state.loading = false;
    })

    // updateSessionNarrative()
    .addCase(updateSessionNarrative.pending, (state: SessionState, action: any) => {
      state.error = null;
      state.loading = true;
      state.narrativeUpdateRequest = {
        sessionId: action.meta.arg.sessionId,
        contextToken: action.meta.arg.narrativeContextToken,
        requestId: action.meta.requestId,
      };
    })
    .addCase(updateSessionNarrative.fulfilled, (state: SessionState, action: any) => {
      const request = state.narrativeUpdateRequest;
      const { sessionId, narrativeContextToken } = action.meta.arg;
      const isCurrentRequest =
        request?.requestId === action.meta.requestId &&
        request?.sessionId === sessionId &&
        (!narrativeContextToken || request?.contextToken === narrativeContextToken) &&
        state.current?.id === sessionId &&
        (!narrativeContextToken || state.narrativeContextToken === narrativeContextToken);

      if (!isCurrentRequest) return;
      state.current = {
        ...state.current,
        ...action.payload,
      };
      state.loading = false;
      state.narrativeUpdateRequest = null;
    })
    .addCase(updateSessionNarrative.rejected, (state: SessionState, action: any) => {
      if (state.narrativeUpdateRequest?.requestId !== action.meta.requestId) return;
      state.error = action.error;
      state.loading = false;
      state.narrativeUpdateRequest = null;
      toast.error(action.error.message);
    })

    // updateSessionHeaderInfo()
    .addCase(updateSessionHeaderInfo.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateSessionHeaderInfo.fulfilled, (state: SessionState, action: any) => {
      const requestData = action.meta.arg.data ?? {};
      const requestIncludesBreaks = Object.prototype.hasOwnProperty.call(requestData, 'breaks');
      const breaks =
        action.payload.breaks ??
        (requestIncludesBreaks ? requestData.breaks : state.current?.breaks);

      state.current = {
        ...state.current,
        ...action.payload,
        breaks,
        // Check if goals_and_trials is an array (for legacy sessions). If so, retain the existing goals_and_trials.
        // If not, merge the new goals_and_trials with the existing ones to ensure both saved and unsaved goals are preserved.
        goals_and_trials: Array.isArray(action.payload.goals_and_trials)
          ? state.current?.goals_and_trials
          : {
              ...state.current?.goals_and_trials,
              ...action.payload.goals_and_trials,
            },
      };
      state.loading = false;
      if (action.meta.arg.updateGPTNarrativeFlag) {
        state.gptNarrativeFlag = state.gptNarrativeFlag + 1;
      }
    })
    .addCase(updateSessionHeaderInfo.rejected, (state: SessionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })

    // getSession()
    // Guard by requestId (mirrors the client.slice.ts modifier-matrix pattern) so a stale
    // detail response can never overwrite a newer session that is already active.
    .addCase(getSession.pending, (state: SessionState, action: any) => {
      state.loading = true;
      state.error = null;
      state.narrativeContextToken = null;
      state.narrativeUpdateRequest = null;
      state.detailRequestId = action.meta.requestId;
    })
    .addCase(
      getSession.fulfilled,
      (state: SessionState, action: PayloadAction<any> & { meta: { requestId: string } }) => {
        if (action.meta.requestId !== state.detailRequestId) return;
        state.current = action.payload;
        state.narrativeContextToken = null;
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(getSession.rejected, (state: SessionState, action: any) => {
      if (action.meta.requestId !== state.detailRequestId) return;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // reviewSession()
    .addCase(reviewSession.pending, (state: SessionState) => {
      state.error = null;
    })
    .addCase(reviewSession.fulfilled, (state: SessionState, action: any) => {
      if (!state.current) return;
      state.current = {
        ...state.current,
        status_id: BTSessionStatus.EditsRequested,
        review_comments: action.meta.arg.data.review_comments,
      };
      state.error = null;
    })
    .addCase(reviewSession.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
    })

    // createTrial()
    .addCase(createTrial.pending, (state: SessionState) => {
      state.trialLoading = true;
      state.error = null;
    })
    .addCase(createTrial.fulfilled, (state: SessionState) => {
      state.trialLoading = false;
    })
    .addCase(createTrial.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
      state.trialLoading = false;
    })

    // updateTrial()
    .addCase(updateTrial.pending, (state: SessionState) => {
      state.trialLoading = true;
      state.error = null;
    })
    .addCase(updateTrial.fulfilled, (state: SessionState) => {
      state.trialLoading = false;
    })
    .addCase(updateTrial.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
      state.trialLoading = false;
    })

    // deleteTrial()
    .addCase(deleteTrial.pending, (state: SessionState) => {
      state.trialLoading = true;
      state.error = null;
    })
    .addCase(deleteTrial.fulfilled, (state: SessionState) => {
      state.trialLoading = false;
    })
    .addCase(deleteTrial.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
      state.trialLoading = false;
    })

    // createCQAReview()
    .addCase(createCQAReview.pending, (state: SessionState) => {
      state.reviewLoading = true;
      state.error = null;
    })
    .addCase(createCQAReview.fulfilled, (state: SessionState, action: PayloadAction) => {
      state.reviewLoading = false;
      if (!state.current) return;
      state.current = {
        ...state.current,
        clinical_qa_review: action.payload,
      };
    })
    .addCase(createCQAReview.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.reviewLoading = false;
      toast.error(action.error.message);
    })

    // updateCQAReview()
    .addCase(updateCQAReview.pending, (state: SessionState) => {
      state.reviewLoading = true;
      state.error = null;
    })
    .addCase(
      updateCQAReview.fulfilled,
      (state: SessionState, action: PayloadAction<IClinicalQAReview>) => {
        state.reviewLoading = false;
        if (!state.current) return;
        state.current = {
          ...state.current,
          clinical_qa_review: action.payload,
        };
      },
    )
    .addCase(updateCQAReview.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.reviewLoading = false;
      toast.error(action.error.message);
    })

    // stopCQAReview()
    .addCase(stopCQAReview.pending, (state: SessionState) => {
      state.reviewLoading = true;
      state.error = null;
    })
    .addCase(
      stopCQAReview.fulfilled,
      (state: SessionState, action: PayloadAction<IClinicalQAReview>) => {
        state.reviewLoading = false;
        if (!state.current) return;
        state.current = {
          ...state.current,
          clinical_qa_review: action.payload,
        };
      },
    )
    .addCase(stopCQAReview.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.reviewLoading = false;
      toast.error(action.error.message);
    })

    // acknowledgeCQAReview()
    .addCase(acknowledgeCQAReview.pending, (state: SessionState) => {
      state.reviewLoading = true;
      state.error = null;
    })
    .addCase(
      acknowledgeCQAReview.fulfilled,
      (state: SessionState, action: PayloadAction<IClinicalQAReview>) => {
        state.clinicalReview = action.payload;
        state.reviewLoading = false;
      },
    )
    .addCase(acknowledgeCQAReview.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.reviewLoading = false;
      toast.error(action.error.message);
    })

    // discourseCQAReview()
    .addCase(discourseCQAReview.pending, (state: SessionState) => {
      state.reviewLoading = true;
      state.error = null;
    })
    .addCase(
      discourseCQAReview.fulfilled,
      (state: SessionState, action: PayloadAction<IClinicalQAReviewDiscourse>) => {
        state.reviewLoading = false;
        if (!state.current) return;
        state.current = {
          ...state.current,
          clinical_qa_review: {
            ...state.current?.clinical_qa_review,
            discourses: [
              ...(state.current.clinical_qa_review?.discourses ?? []),
              action.payload,
            ],
          },
          status_id: action.payload.is_edit_requested
            ? BTSessionStatus.EditsRequested
            : BTSessionStatus.Completed,
        };
      },
    )
    .addCase(discourseCQAReview.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.reviewLoading = false;
      toast.error(action.error.message);
    })
    // getUnacknowledgedSessions()
    .addCase(getUnacknowledgedSessions.pending, (state: SessionState) => {
      state.notificationsLoading = true;
      state.error = null;
    })
    .addCase(
      getUnacknowledgedSessions.fulfilled,
      (state: SessionState, action: PayloadAction<IAwaitingAcknowledgement[]>) => {
        state.unacknowledgedSessions = action.payload;
        state.error = null;
        state.notificationsLoading = false;
      },
    )
    .addCase(getUnacknowledgedSessions.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.notificationsLoading = false;
      toast.error(action.error.message);
    })

    // getTrialSummary()
    .addCase(getTrialSummary.pending, (state: SessionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(getTrialSummary.fulfilled, (state: SessionState, action: PayloadAction<any[]>) => {
      state.trialSummary = action.payload;
      state.loading = false;
    })
    .addCase(getTrialSummary.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getMultiTrialSummary()
    .addCase(
      getMultiTrialSummary.pending,
      (state: SessionState, action: { meta: { requestId: string } }) => {
        state.error = null;
        state.loading = true;
        state.multiTrialSummaryLoading = true;
        state.multiTrialSummaryError = null;
        state.multiTrialSummaryRequestId = action.meta.requestId;
      },
    )
    .addCase(
      getMultiTrialSummary.fulfilled,
      (state: SessionState, action: PayloadAction<any[], string, { requestId: string }>) => {
        if (action.meta.requestId !== state.multiTrialSummaryRequestId) return;
        state.multiTrialSummary = action.payload;
        state.loading = false;
        state.multiTrialSummaryLoading = false;
        state.multiTrialSummaryRequestId = null;
      },
    )
    .addCase(
      getMultiTrialSummary.rejected,
      (
        state: SessionState,
        action: { error: { message?: string }; meta: { requestId: string } },
      ) => {
        if (action.meta.requestId !== state.multiTrialSummaryRequestId) return;
        state.error = action.error;
        state.loading = false;
        state.multiTrialSummaryLoading = false;
        state.multiTrialSummaryError = action.error.message ?? 'Unable to load dashboard report';
        state.multiTrialSummaryRequestId = null;
        toast.error(state.multiTrialSummaryError);
      },
    )

    .addCase(
      getDashboardOptions.pending,
      (state: SessionState, action: { meta: { requestId: string } }) => {
        state.dashboardOptions = [];
        state.dashboardOptionsLoading = true;
        state.dashboardOptionsError = null;
        state.dashboardOptionsRequestId = action.meta.requestId;
      },
    )
    .addCase(
      getDashboardOptions.fulfilled,
      (
        state: SessionState,
        action: PayloadAction<IDataDashboardGoalOption[], string, { requestId: string }>,
      ) => {
        if (action.meta.requestId !== state.dashboardOptionsRequestId) return;
        state.dashboardOptions = action.payload;
        state.dashboardOptionsLoading = false;
      },
    )
    .addCase(
      getDashboardOptions.rejected,
      (state: SessionState, action: { error: { message?: string }; meta: { requestId: string } }) => {
        if (action.meta.requestId !== state.dashboardOptionsRequestId) return;
        state.dashboardOptions = [];
        state.dashboardOptionsLoading = false;
        state.dashboardOptionsError = action.error.message ?? 'Unable to load dashboard options';
      },
    )

    // deleteCQAReview()
    .addCase(deleteClinicalReview.pending, (state: SessionState) => {
      state.reviewLoading = true;
      state.error = null;
    })
    .addCase(deleteClinicalReview.fulfilled, (state: SessionState) => {
      state.reviewLoading = false;
    })
    .addCase(deleteClinicalReview.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.reviewLoading = false;
      toast.error(action.error.message);
    })

    // getReviewSummary()
    .addCase(getReviewSummary.pending, (state: SessionState) => {
      state.reviewSummaryLoading = true;
      state.error = null;
    })
    .addCase(
      getReviewSummary.fulfilled,
      (state: SessionState, action: PayloadAction<ISessionReviewSummary[]>) => {
        state.reviewSummary = action.payload;
        state.reviewSummaryLoading = false;
      },
    )
    .addCase(getReviewSummary.rejected, (state: SessionState, action: any) => {
      state.reviewSummary = [];
      state.error = action.error;
      state.reviewSummaryLoading = false;
      toast.error(action.error.message);
    })

    // changeSessionStatus()
    .addCase(changeSessionStatus.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(changeSessionStatus.fulfilled, (state: SessionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Session note status changed successfully', {
        toastId: 'session-note-status-success',
      });
    })
    .addCase(changeSessionStatus.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message, { toastId: 'session-note-status-error' });
    })

    // makeSessionEditable()
    .addCase(makeSessionEditable.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(makeSessionEditable.fulfilled, (state: SessionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Session note is now editable');
    })
    .addCase(makeSessionEditable.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // getSessionsWithClaimStatus()
    .addCase(getSessionsWithClaimStatus.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getSessionsWithClaimStatus.fulfilled,
      (state: SessionState, action: PayloadAction<ISessionMinifiedWithClaimHistory[]>) => {
        state.loading = false;
        state.sessionsReadyForQA = action.payload.filter(
          (session) =>
            session.status !== BTSessionStatus.Started &&
            session.status !== BTSessionStatus.AwaitingGuardianSignature,
        );
        state.sessionsWithClaimStatus = action.payload
          .filter(
            (session) =>
              session.status !== BTSessionStatus.Started &&
              session.status !== BTSessionStatus.AwaitingGuardianSignature,
          )
          .map(
            (session) =>
              ({
                id: session.id,
                date: moment(session.date_start).format(),
                status:
                  session.claim_history?.length > 0
                    ? session.claim_history[0].status
                    : session.status === BTSessionStatus.Completed ||
                        (session.status === BTSessionStatus.AwaitingReview &&
                          !session.clinical_qa_review)
                      ? SessionClaimStatus.Unclaimed
                      : SessionClaimStatus.NotReady,
                selected: false,
                isUnderClinicalReview:
                  session.clinical_qa_review &&
                  (session.status === BTSessionStatus.AwaitingReview ||
                    session.status === BTSessionStatus.EditsRequested),
                location: session.location,
                duration: session.expected_duration ?? 0,
                clinical_relationship: session.clinical_relationship,
                provider_name: `${session?.bcba?.first_name} ${session?.bcba?.last_name}`,
                session_provider: `${session?.session_provider?.first_name} ${session?.session_provider?.last_name}`,
                provider_id: session?.bcba?.id,
                author_id: session?.session_provider?.id,
                timeZone: session.utc_offset_date_start
                  ? toUtcOffsetDisplay(asDefinedOr(session.utc_offset_date_start, 0))
                  : '0:0',
                sessionStatus: session.status,
                procedureCode: ServiceCodes.SessionProcedureCode,
                bay: session.bay?.replace('BAY ', ''),
                isAwaitingFinalization:
                  session?.status === BTSessionStatus.AwaitingReview &&
                  session?.clinical_qa_review?.status === CQAReviewStatus.Acknowledged,
              }),
          );
      },
    )
    .addCase(getSessionsWithClaimStatus.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'sessions-with-claim-status-error' });
    })

    // deleteSession()
    .addCase(deleteSession.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteSession.fulfilled, (state: SessionState, action: any) => {
      state.loading = false;
      state.current = null;
      if (!action.meta.arg?.suppressToast) {
        toast.success('Session deleted successfully');
      }
    })
    .addCase(deleteSession.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // endSessionEarly()
    .addCase(endSessionEarly.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(endSessionEarly.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      if (!state.current) return;
      state.loading = false;
      state.current = {
        ...state.current,
        narrative: action.payload.narrative,
        expected_duration: action.payload.duration,
      };
    })
    .addCase(endSessionEarly.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // createDurationEvent()
    .addCase(createDurationEvent.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createDurationEvent.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.loading = false;
      if (state.current) updateTrialInSession(state.current, action.payload);
    })
    .addCase(createDurationEvent.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateDurationEvent()
    .addCase(updateDurationEvent.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateDurationEvent.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.loading = false;
      if (state.current) updateTrialInSession(state.current, action.payload);
    })
    .addCase(updateDurationEvent.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // deleteDurationEvent()
    .addCase(deleteDurationEvent.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteDurationEvent.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.loading = false;
      if (state.current) updateTrialInSession(state.current, action.payload);
    })
    .addCase(deleteDurationEvent.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // createRateEvent()
    .addCase(createRateEvent.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createRateEvent.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.loading = false;
      if (state.current) updateTrialInSession(state.current, action.payload);
    })
    .addCase(createRateEvent.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateRateEvent()
    .addCase(updateRateEvent.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateRateEvent.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.loading = false;
      if (state.current) updateTrialInSession(state.current, action.payload);
    })
    .addCase(updateRateEvent.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // deleteRateEvent()
    .addCase(deleteRateEvent.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteRateEvent.fulfilled, (state: SessionState, action: PayloadAction<any>) => {
      state.loading = false;
      if (state.current) updateTrialInSession(state.current, action.payload);
    })
    .addCase(deleteRateEvent.rejected, (state: SessionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // backdateSession()
    .addCase(backdateSession.pending, (state: SessionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(backdateSession.fulfilled, (state: SessionState) => {
      state.loading = false;
      state.error = null;
      toast.success('The start date of session note has been updated');
    })
    .addCase(backdateSession.rejected, (state: SessionState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    });
}

export const sessionActions = { ...slice.actions, ...extraActions };
export const sessionReducer = slice.reducer;
